import {
  DataCard,
  ReportSection,
  ThumbRatingContainer,
  getName,
} from '@/reports/index';
import { serializedTypes } from '@/reports/types';
import { BusinessCore } from '@/reports/types/businesses';
import { Text, Stack } from '@/theme/index';
import { PotentialOwnerCard } from './PotentialOwnerCard';
import { PotentialOwnersDropdown } from './PotentialOwnersDropdown';

export interface PotentialOwnersProps {
  /** Serialized person objects that contain data for the rest of the report. */
  potentialOwners: serializedTypes.SerializedPerson[];
  /** The index for the current potential owner whose data is being shown in the report. */
  potentialOwnerIndex: number;
  /** Callback fired when the potential owner is changed in the dropdown. */
  setPotentialOwnerIndex: (index: number) => void;
  /** The name of the person whose report this is. */
  personName: string;
  /** The permalink for the current report. */
  permalink: string;
  /** Business associated with phone number */
  business?: BusinessCore;
}

/**
 * A report section with a dropdown and data card to allow a user to select
 * a potential owner of the current report and see some basic info about them.
 */
export const PotentialOwners: React.FC<PotentialOwnersProps> = ({
  potentialOwners,
  potentialOwnerIndex = 0,
  setPotentialOwnerIndex,
  personName,
  permalink,
  business,
}) => {
  const currentPotentialOwner = potentialOwners[potentialOwnerIndex];
  const titleName = getName(currentPotentialOwner);

  return (
    <ReportSection
      title="Potential Owner"
      titleName={titleName}
      hasContent={potentialOwners.length > 0 || Boolean(business)}
      sectionId="potential-owners-section"
      customTitleStyle={{
        fontWeight: 'regular',
        '.title-name': { fontWeight: 'bold' },
      }}
      personName={personName}
    >
      {potentialOwners.length > 0 && (
        <Stack>
          <DataCard cardBodyWidth="100%">
            <Text variant="h2">
              {potentialOwners.length} Potential Owners Identified
            </Text>
            <Text variant="h4">
              Select a potential owner ({potentialOwnerIndex + 1} of{' '}
              {potentialOwners.length}) to view more info
            </Text>
            <PotentialOwnersDropdown
              potentialOwners={potentialOwners}
              potentialOwnerIndex={potentialOwnerIndex}
              setPotentialOwnerIndex={setPotentialOwnerIndex}
            />
          </DataCard>
          <ThumbRatingContainer
            item={currentPotentialOwner}
            component={PotentialOwnerCard}
            dataPropKey="currentPotentialOwner"
            permalink={permalink}
            cardProps={{ owner: currentPotentialOwner }}
          />
        </Stack>
      )}
      {!potentialOwners.length && business && (
        <DataCard cardBodyWidth="100%">
          <Text variant="h2">1 Potential Owner Identified</Text>
          <Text variant="h4">{business.names[0].name}</Text>
          {business?.addresses[0]?.full && (
            <Text>{business.addresses[0].full}</Text>
          )}
        </DataCard>
      )}
    </ReportSection>
  );
};
